import { injectable } from 'inversify';
import type {
  ActorsResponse,
  AgendaDebate,
  AgendaOverviewResponse,
  AgendaResponse,
  AgendaWeeksResponse,
  AppResponse,
  DebateLocation,
  DebateResponse,
  Document,
  DownloadOption,
  FAQ,
  LiveNotification,
  Politician,
  Terminology,
} from '@debatdirect/common/types/debatdirect-typings';

import { getDataOrThrow } from '../utils/api';
import env from '../env';
import { logError } from '../logger';
import { transformAgendaData, transformAgendaVotingsData, transformAppData, transformDebateData } from '../utils/apiDataTransformers';
import { startDateCompareFn } from '../utils/debate';
import type { AppDebate } from '../../types/debate';
import { ApiError } from '../errors/ApiError';

@injectable()
export default class ApiService {
  protected async fetch<T>(path: string, method: string = 'GET', body?: string, headers?: Record<string, string>, useApi = false) {
    // To be GDPR-compliant, we send all post, put, patch and delete requests directly to the API environment
    const url = `${useApi ? env.APP_API_BASE_URL : env.APP_CDN_BASE_URL}${path}`;
    headers = headers || {};

    try {
      if (body) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(url, { method, body, headers });

      return (await getDataOrThrow(response)) as T;
    } catch (error: unknown) {
      const isNotFoundError = error instanceof ApiError && error.code === 404;

      // ignore logging 404 errors
      if (!isNotFoundError) {
        logError('ApiService', `Failed to fetch ${url}`, { error });
      }

      throw error; // re-throw
    }
  }

  /**
   * Get data about app versions.
   */
  async getAppVersions() {
    return this.fetch('/values/version-check');
  }

  /**
   * Get data about user location.
   */
  async getWhoAmI() {
    return this.fetch('/whoami');
  }

  /**
   * Get agenda overview.
   */
  async getAgendaOverview() {
    return this.fetch<AgendaOverviewResponse>('/agenda/overview');
  }

  /**
   * Get voting overview.
   */
  async getVotings() {
    const votings = await this.fetch<AgendaWeeksResponse>('/agenda/votings');

    return transformAgendaVotingsData(votings);
  }

  /**
   * Get dated components.
   */
  async getDatedComponents(date: string) {
    return Promise.all([this.getDatedActors(date), this.getDatedAgendaLegacy(date)]);
  }

  async getLiveNotifications() {
    return this.fetch<LiveNotification[]>('/agenda/live-notifications');
  }

  /**
   * Get available video download qualities
   * @param {String} debateId
   */
  async getDebateDownloadQualities(debateId: string): Promise<DownloadOption[]> {
    const options: { qualities: DownloadOption[] } = await this.fetch(`/debates/${debateId}/qualities`);

    return options.qualities || [];
  }

  /**
   * Requests all actors (hashmap of parties and politicians)
   */
  async getActors() {
    return this.fetch<ActorsResponse>('/actors');
  }

  /**
   * Get agenda for a specific date.
   */
  async getDatedActors(date: string) {
    return this.fetch<ActorsResponse>(`/actors/${date}`);
  }

  /**
   * Requests all parties
   */
  async getParties() {
    return this.fetch('/actors/parties');
  }

  /**
   * Requests all politicians
   */
  async getPoliticians() {
    return this.fetch('/actors/politicians');
  }

  // Agenda

  /**
   * Requests all agenda data (hashmap of categories, debates and documents)
   */
  async getAgenda() {
    const data = await this.fetch<AgendaResponse & { agendaFetchTime: number }>('/agenda');
    data.agendaFetchTime = Date.now();
    return data;
  }

  /**
   * Get agenda for a specific date.
   */
  async getDatedAgendaLegacy(date: string) {
    const agenda = await this.fetch<AgendaResponse & { agendaFetchTime: number }>(`/agenda/${date}`);
    agenda.agendaFetchTime = Date.now();

    return agenda;
  }

  /**
   * Get agenda for a specific date.
   */
  async getDatedAgenda(date: string) {
    const agenda = await this.fetch<AgendaResponse & { agendaFetchTime: number }>(`/agenda/${date}`);
    agenda.agendaFetchTime = Date.now();

    return transformAgendaData(agenda);
  }

  /**
   * Get politician data.
   */
  async getPoliticianData(politicianId: string, date?: string) {
    return this.fetch<Politician>(date ? `/actors/${date}/politicians/${politicianId}` : `/actors/politicians/${politicianId}`);
  }

  /**
   * Requests all categories
   */
  async getCategories() {
    return this.fetch('/agenda/categories');
  }

  /**
   * Requests all debates
   */
  async getDebates() {
    return this.fetch<AgendaDebate[]>('/agenda/debates');
  }

  /**
   * Requests detailed debate info
   */
  async getDebateLegacy(debateDate: string, debateId: string) {
    return this.fetch<DebateResponse>(`/agenda/${debateDate}/debates/${debateId}`);
  }

  /**
   * Requests detailed debate info and transform into app format
   */
  async getDebate(debateId: string): Promise<AppDebate> {
    const debate = await this.fetch<DebateResponse>(`/debates/${debateId}`);

    return transformDebateData(debate);
  }

  /**
   * Try to resolve debat gemist URL.
   */
  async getMissingDebateData(parameters: string) {
    return this.fetch(`/debatgemist?${parameters}`);
  }

  /**
   * Requests all documents
   */
  async getDocuments() {
    return this.fetch<Document[]>('/agenda/documents');
  }

  // App

  /**
   * Requests all static data (hashmap of faqs, locations and terminology)
   */
  async getApp() {
    const app = await this.fetch<AppResponse>('/app');

    return transformAppData(app);
  }

  /**
   * Requests all static data (hashmap of faqs, locations and terminology)
   */
  async getAppLegacy() {
    return this.fetch<AppResponse>('/app');
  }

  /**
   * Requests all terminology
   */
  async getTerminology() {
    return this.fetch<Terminology>('/app/terminology');
  }

  /**
   * Requests all faqs
   */
  async getFaq() {
    return this.fetch<FAQ>('/app/faq');
  }

  /**
   * Requests all locations
   */
  async getLocations() {
    return this.fetch<DebateLocation[]>('/locations');
  }

  // Notifications

  /**
   * Requests all notifications from the given user (identifier)
   */
  async getNotificationsV1(deviceToken: string) {
    return this.fetch(`/notifications`, 'GET', undefined, { ClientId: deviceToken });
  }

  /**
   * Removes a specific notification
   */
  async deleteNotificationV1(id: string, deviceToken: string) {
    return this.fetch(`/notifications/${id}`, 'DELETE', undefined, { ClientId: deviceToken });
  }

  /**
   * Obtain endpoint identifier from the given deviceToken
   */
  async obtainEndpointIdentifier(deviceToken: string) {
    return this.fetch(`/v2/endpoint/obtain`, 'POST', JSON.stringify({ deviceToken }));
  }

  /**
   * Requests all notifications from the given user (identifier)
   */
  async getNotifications(id: string) {
    return this.fetch(`/v2/notifications/${id}`);
  }

  /**
   * Registers a notification
   */
  async putNotifications(payload: object) {
    return this.fetch('/v2/notifications', 'PUT', JSON.stringify(payload));
  }

  /**
   * Sends the support form data
   */
  async postSupport(payload: object) {
    return this.fetch('/support', 'POST', JSON.stringify(payload), undefined, true);
  }

  /**
   * Get debates across multiple weeks
   */
  async getAgendaWeeks() {
    const agenda = await this.fetch<AgendaWeeksResponse>(`/agenda/weeks/today`);
    return agenda.debates.map(transformDebateData).sort(startDateCompareFn);
  }

  /**
   * Get debate summary
   */
  async getDebateSummary(debateId: string) {
    const debateSummary = await this.fetch<AgendaDebate>(`/debates/${debateId}/summary`);

    return transformDebateData(debateSummary);
  }
}
